// router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import ForgotPassword from '../components/ForgotPassword.vue';
import ResetPassword from '../components/ResetPassword.vue';
import Login from '../components/Login.vue';
import Signup from '../components/Signup.vue';
import Terms from '../components/tnc.vue';
import Users from '../components/business/Users.vue';
import EditUser from '../components/business/EditUser.vue';
import InviteUser from '../components/business/InviteUser.vue';
import InviteMany from '../components/business/InviteMany.vue';
import CreateLink from '../components/business/CreateLink.vue';
import BusinessProfile from '../components/business/BusinessProfile.vue';
import InviteLanding from '../components/business/InviteLanding.vue';
import ViewPayments from '../components/transactions/ViewPayments.vue';
import OrderHistory from '../components/transactions/OrderHistory.vue';
import Invoices from '../components/transactions/Invoices.vue';
import InvoiceDetail from '../components/transactions/InvoiceDetail.vue';
import IndividualProfile from '../components/profile/Profile.vue';
import ChangePassword from '../components/profile/Password.vue';
import NewRequest from '../components/order/NewRequest.vue';
import TrackOrder from '../components/order/TrackOrder.vue';
import ApiKeys from '../components/developers/ApiKeys.vue';
import VerifyEmail from '../components/VerifyEmail.vue';
import { useAuthStore } from '../store/authStore';

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: VerifyEmail,
  },
  {
    path: '/forgot',
    name: 'ForgotPassword',
    component: ForgotPassword,
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
  },
  // ── Admin-only routes ────────────────────────────────────────────────────────
  {
    path: '/business',
    name: 'Team',
    component: Users,
    meta: { roles: ['admin'] },
  },
  {
    path: '/users',
    name: 'Users',
    component: Users,
    meta: { roles: ['admin'] },
  },
  {
    path: '/edituser/:id',
    name: 'EditUser',
    component: EditUser,
    meta: { roles: ['admin'] },
  },
  {
    path: '/add_user',
    name: 'InviteUser',
    component: InviteUser,
    meta: { roles: ['admin'] },
  },
  {
    path: '/bulk_invite',
    name: 'InviteMany',
    component: InviteMany,
    meta: { roles: ['admin'] },
  },
  {
    path: '/create_link',
    name: 'CreateLink',
    component: CreateLink,
    meta: { roles: ['admin'] },
  },
  {
    path: '/business-profile',
    name: 'BusinessProfile',
    component: BusinessProfile,
    meta: { roles: ['admin'] },
  },
  {
    path: '/profile',
    name: 'IndividualProfile',
    component: IndividualProfile,
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
  },
  {
    path: '/developers/api-keys',
    name: 'ApiKeys',
    component: ApiKeys,
  },
  {
    path: '/transactions/payments',
    name: 'ViewPayments',
    component: ViewPayments,
  },
  {
    path: '/transactions/orders',
    name: 'OrderHistory',
    component: OrderHistory,
  },
  {
    // Statement page was removed; keep old links working.
    path: '/transactions/statement',
    redirect: '/invoices',
  },
  {
    path: '/invoices',
    name: 'Invoices',
    component: Invoices,
  },
  {
    path: '/invoices/:id',
    name: 'InvoiceDetail',
    component: InvoiceDetail,
  },
  {
    path: '/request',
    name: 'NewRequest',
    component: NewRequest,
  },
  {
    path: '/track/:id',
    name: 'TrackOrder',
    component: TrackOrder,
  },
  {
    path: '/track',
    name: 'TrackOrderQuery',
    component: TrackOrder,
  },
  {
    path: '/reset/:id',
    name: 'ResetPassword',
    component: ResetPassword,
  },
  {
    path: '/reset',
    name: 'ResetPasswordQuery',
    component: ResetPassword,
  },
  {
    path: '/onboard/:id',
    name: 'InviteLanding',
    component: InviteLanding,
  },
  {
    path: '/onboard',
    name: 'InviteLandingQuery',
    component: InviteLanding,
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
  },
  { path: '/', redirect: '/request' }

];

const router = createRouter({
  history: createWebHistory(),
  routes,
});



// Validate the HttpOnly-cookie session exactly once before the first guarded
// navigation, so deep-links / refreshes resolve auth from the cookie rather than
// bouncing to /login on stale (or missing) localStorage.
let sessionInit: Promise<void> | null = null;

router.beforeEach(async (to, from, next) => {
  const publicPages = ['Signup','Login','ForgotPassword','ResetPassword','ResetPasswordQuery','InviteLanding','InviteLandingQuery','Terms','VerifyEmail'];

  const authRequired = !publicPages.includes(to.name as string);

  const store = useAuthStore();

  if (!sessionInit) sessionInit = store.init();
  await sessionInit;

  if (authRequired && !store.isAuthenticated) {
    return next('/login');
  }

  // Role guard — check meta.roles if present
  const requiredRoles = (to.meta?.roles as string[] | undefined);
  if (requiredRoles && requiredRoles.length > 0) {
    if (!requiredRoles.includes(store.user?.role ?? '')) {
      return next('/request');
    }
  }

  next();
});

export default router;
